import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Article } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ArticleHero } from '@/components/journal/article-hero'
import { ArticleCover } from '@/components/journal/article-cover'
import { ArticleBody } from '@/components/journal/article-body'
import { ArticleQuote } from '@/components/journal/article-quote'
import { ArticleNext } from '@/components/journal/article-next'
import { articles, getArticle } from '@/constants/journal'

interface ArticlePageProps {
    article: Article
    related: Article[]
}

const ArticlePage: FC<ArticlePageProps> = ({ article, related }): ReactNode => {
    /* Titles carry a closing full stop; the page title reads cleaner without
       it once the site name is appended. */
    const pageTitle = article.title.replace(/\.$/, '')

    return (
        <Layout theme='light' className='bg-paper text-ink'>
            <Seo
                title={pageTitle}
                description={article.excerpt}
                ogImage={article.image}
            />

            <ArticleHero article={article} />
            <ArticleCover src={article.image} alt={article.title} />
            <ArticleBody article={article} />
            <ArticleQuote quote={article.excerpt} />
            <ArticleNext articles={related} />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: articles.map((article) => ({ params: { slug: article.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<ArticlePageProps> = (context) => {
    const slug = typeof context.params?.slug === 'string' ? context.params.slug : ''
    const article = getArticle(slug)

    if (!article) {
        return { notFound: true }
    }

    const current = articles.findIndex((entry) => entry.slug === article.slug)

    const related = [1, 2, 3].map((offset) => {
        return articles[(current + offset) % articles.length]
    })

    return {
        props: { article, related }
    }
}

export default ArticlePage
